import { useState, useEffect } from 'react'
import { Menu, X } from 'lucide-react'

interface NavbarProps {
  onNavigate: (page: string) => void
}

export const Navbar: React.FC<NavbarProps> = ({ onNavigate }) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  const navLinks = [
    { label: 'Expertise', href: '#expertise' },
    { label: 'About', href: '#about' },
    { label: 'Insights', href: '#insights' },
    { label: 'Contact', href: '#contact' },
  ]

  const handleNavClick = (href: string) => {
    onNavigate(href)
    setIsMobileMenuOpen(false)
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
      role="banner"
    >
      <nav
        className="container-wide py-4"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault()
              onNavigate('home')
            }}
            className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 rounded-lg"
            aria-label="Learning Xperience Consulting - Home"
          >
            {/* Logo Icon */}
            <img
              src="/logoLXC.png"
              alt=""
              className="h-10 sm:h-12 w-auto"
              aria-hidden="true"
            />
            {/* Company Name */}
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-slate-900 font-bold text-lg tracking-tight">
                Learning <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-amber">X</span>perience
              </span>
              <span className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                Consulting
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center gap-8" role="menubar">
            {navLinks.map((link) => (
              <li key={link.href} role="none">
                <a
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault()
                    handleNavClick(link.href)
                  }}
                  className="text-slate-600 hover:text-brand-blue font-medium transition-colors focus:outline-none focus-visible:text-brand-blue"
                  role="menuitem"
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li role="none">
              <button
                onClick={() => handleNavClick('#contact')}
                className="btn-primary text-sm px-5 py-2"
                role="menuitem"
              >
                Get Started
              </button>
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-slate-600 hover:text-brand-blue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue rounded-lg"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? (
              <X size={24} aria-hidden="true" />
            ) : (
              <Menu size={24} aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          id="mobile-menu"
          className={`md:hidden fixed inset-0 top-[72px] bg-white transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          aria-hidden={!isMobileMenuOpen}
        >
          <ul
            className="flex flex-col items-center justify-center gap-8 h-full"
            role="menu"
          >
            {navLinks.map((link) => (
              <li key={link.href} role="none">
                <a
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault()
                    handleNavClick(link.href)
                  }}
                  className="text-2xl font-semibold text-slate-900 hover:text-brand-blue transition-colors focus:outline-none focus-visible:text-brand-blue"
                  role="menuitem"
                  tabIndex={isMobileMenuOpen ? 0 : -1}
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li role="none">
              <button
                onClick={() => handleNavClick('#contact')}
                className="btn-primary text-lg px-8 py-3"
                role="menuitem"
                tabIndex={isMobileMenuOpen ? 0 : -1}
              >
                Get Started
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  )
}
